import React from "react";
import { CS_TEL_HREF, CS_PHONE_DISPLAY, AUTO_PHONE_DISPLAY } from "@/brand";
import { LegalLayout } from "@/pages/legal/LegalLayout";

// SMS terms section here mirrors Twilio's A2P 10DLC "sample terms" so brand
// review passes on the first submission. The rental terms mirror what the
// checkout `TermsModal` shows the customer at booking.
const TermsOfService = () => (
  <LegalLayout
    title="Terms of Service"
    subtitle="The agreement between you and Enchanted Gear Rentals when you book a rental or receive SMS updates."
    updated="June 14, 2026"
    seoDesc="Enchanted Gear Rentals terms and conditions covering bookings, deposits, cancellations, delivery, and the SMS/WhatsApp messaging program."
  >
    <p>
      These Terms of Service ("Terms") govern your use of{" "}
      <a href="https://enchantedgearrentals.com">enchantedgearrentals.com</a>,
      our rental services, and our SMS and WhatsApp messaging program (the
      "Program"). By booking a rental or opting into the Program, you agree to
      these Terms and to our{" "}
      <a href="/privacy">Privacy Policy</a>. If you do not agree, please do not
      use the service.
    </p>

    <h2>1. Who We Are</h2>
    <p>
      "Enchanted Gear Rentals" refers to Enchanted Gear Rentals LLC, a
      family-owned baby gear rental company based in Orlando, Florida. We are
      not affiliated with, endorsed by, or connected to The Walt Disney
      Company or its subsidiaries.
    </p>

    <h2>2. Bookings, Rentals &amp; Deposits</h2>
    <ul>
      <li><strong>Booking window</strong> — reservations open once your payment authorization succeeds. Delivery windows are confirmed by email and, if opted in, by SMS.</li>
      <li><strong>Deposit hold</strong> — Stripe places a temporary hold at booking. Your card is charged in full at the time you accept the quote.</li>
      <li><strong>Delivery &amp; pickup</strong> — we deliver to the front desk / bell services of your Walt Disney World area hotel or vacation rental. You are responsible for ensuring the hotel accepts deliveries on your behalf.</li>
      <li><strong>Care of gear</strong> — you agree to use the equipment as intended, follow all manufacturer safety guidance, and return it in the condition you received it, minus reasonable wear.</li>
      <li><strong>Damage &amp; loss</strong> — you authorize us to charge the card on file for damage beyond normal wear, missing accessories, or replacement of unreturned gear. You will be sent a photo-documented invoice before any charge.</li>
    </ul>

    <h2>3. Cancellations, Changes &amp; Refunds</h2>
    <ul>
      <li><strong>Peace of Mind</strong> — if you added Peace of Mind coverage at checkout (20% of your rental subtotal), you may cancel any time before delivery for a full refund of your rental fees. The Peace of Mind fee itself and credit-card processing fees are non-refundable.</li>
      <li><strong>Standard cancellations</strong> — bookings cancelled more than 7 days before delivery are refunded in full. Cancellations 3–7 days before delivery receive a 50% refund. Cancellations less than 72 hours before delivery are non-refundable.</li>
      <li><strong>Date changes</strong> — requests to shift dates are subject to inventory availability and admin approval. Approved changes that lower the total will be auto-refunded to the original card within 5 business days.</li>
      <li><strong>No-shows</strong> — if delivery cannot be completed because of an incorrect address or lack of hotel authorization, the reservation is treated as a same-day cancellation.</li>
    </ul>

    <h2>4. Prices &amp; Taxes</h2>
    <p>
      Prices are shown in U.S. dollars and include all applicable Florida
      sales tax at the rate in force on the day of booking. Enchanted Gear
      Rentals remits sales tax on your behalf; tax appears as a separate line
      item on your receipt.
    </p>

    <h2>5. SMS &amp; WhatsApp Messaging Program</h2>
    <div className="callout">
      <strong>Program summary</strong>
      By checking the optional "SMS Updates" box at checkout you consent to
      receive transactional and customer-service messages from Enchanted Gear
      Rentals via SMS (and, if you selected WhatsApp, via WhatsApp) at the
      mobile number you provided — including reservation updates, delivery
      ETAs, driver arrival alerts, pickup reminders, tracking links, and
      customer support. Consent is optional and is not a condition of purchase.
    </div>
    <p>
      <strong>If you do not opt into SMS</strong> — we may attempt to contact
      you by email instead. SMS is our preferred communication method because
      it allows the fastest and easiest delivery and pickup coordination.
      Regardless of your communication choice, you remain responsible for
      being present in your resort lobby for the entire selected delivery and
      pickup window. If we are unable to reach you and you are not present when
      our driver arrives, a waiting or rescheduling fee may apply, or your
      reservation may be cancelled without refund in accordance with these
      Terms.
    </p>
    <p>
      <strong>Driver communication privacy</strong> — all texts and emails
      between you and our delivery team are relayed through Enchanted Gear
      Rentals' business messaging number and business email address. Our
      drivers never see, and are never given access to, your personal phone
      number or email address.
    </p>
    <ul>
      <li><strong>Program purpose</strong> — booking confirmations, delivery windows, driver ETAs, driver arrival alerts, tracking links, pickup reminders, change-request updates, refund confirmations, damage-inspection notes, and post-trip review requests.</li>
      <li><strong>Message frequency</strong> — variable; typically 3–8 messages per rental depending on your itinerary.</li>
      <li><strong>Cost</strong> — messages are sent free of charge from Enchanted Gear Rentals, but message and data rates may apply from your wireless carrier.</li>
      <li><strong>Supported carriers</strong> — AT&amp;T, T-Mobile, Verizon Wireless, Sprint, Boost Mobile, Cricket Wireless, U.S. Cellular, MetroPCS, and most other U.S. carriers. Carriers are not liable for delayed or undelivered messages.</li>
      <li><strong>Opt-out</strong> — reply <strong>STOP</strong> to any message to unsubscribe from all future texts. You will receive one confirmation message and no further texts.</li>
      <li><strong>Help</strong> — reply <strong>HELP</strong> for support information, or contact us at{" "}
        <a href="mailto:info@enchantedgearrentals.com">info@enchantedgearrentals.com</a>{" "}
        or call/text <a href={CS_TEL_HREF}>{CS_PHONE_DISPLAY}</a>.</li>
      <li><strong>Sending number</strong> — automated order updates are sent from <strong>{AUTO_PHONE_DISPLAY}</strong>. This is an automated line; if you call it you will be directed to our customer service team at {CS_PHONE_DISPLAY}.</li>
      <li><strong>Data privacy</strong> — no mobile information will be shared with third parties or affiliates for marketing or promotional purposes. See our{" "}
        <a href="/privacy">Privacy Policy</a> for full details.</li>
    </ul>
    <p>
      You represent that you are the account holder of the mobile number you
      provide (or have authorization from that account holder) and that you are
      at least 13 years old. If your mobile number changes, notify us so we can
      keep your consent record accurate.
    </p>

    <h2>6. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use our service for any unlawful, harassing, or fraudulent purpose.</li>
      <li>Attempt to reverse-engineer, scrape, or interfere with the website or its APIs.</li>
      <li>Impersonate another customer or booking agent.</li>
      <li>Send abusive replies to our SMS or WhatsApp threads. We reserve the right to cancel any reservation associated with abusive behavior.</li>
    </ul>

    <h2>7. Intellectual Property</h2>
    <p>
      All content on this website — including logos, illustrations, product
      photography, copy, and the "Field Journal" articles — is the property of
      Enchanted Gear Rentals or its licensors and is protected by U.S.
      copyright and trademark law. You may not copy or redistribute it without
      written permission.
    </p>

    <h2>8. Warranties &amp; Disclaimers</h2>
    <p>
      Rental equipment is provided "as is." We inspect and sanitize every
      item between rentals and follow manufacturer safety recommendations, but
      we do not warrant that the equipment will be free of every latent defect
      or that it will meet every family's specific need. You are the responsible
      adult supervising the child while the equipment is in use.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Enchanted Gear Rentals' total
      liability for any claim arising from a rental or from these Terms is
      limited to the amount you paid for the rental in question. We are not
      liable for indirect, incidental, consequential, or punitive damages,
      including lost profits or missed vacation activities.
    </p>

    <h2>10. Indemnification</h2>
    <p>
      You agree to indemnify and hold Enchanted Gear Rentals harmless from
      claims arising from your misuse of the equipment, your breach of these
      Terms, or your violation of applicable law.
    </p>

    <h2>11. Governing Law &amp; Disputes</h2>
    <p>
      These Terms are governed by the laws of the State of Florida, without
      regard to conflict-of-laws principles. Any dispute will be resolved in
      the state or federal courts of Orange County, Florida. You and Enchanted
      Gear Rentals waive any right to a jury trial.
    </p>

    <h2>12. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be
      posted here with a new "Last updated" date. Continued use of our services
      after the update means you accept the revised Terms.
    </p>

    <h2>13. Contact</h2>
    <p>
      Enchanted Gear Rentals LLC<br />
      Orlando, Florida, United States<br />
      Email: <a href="mailto:info@enchantedgearrentals.com">info@enchantedgearrentals.com</a><br />
      Customer service (call or text): <a href={CS_TEL_HREF}>{CS_PHONE_DISPLAY}</a><br />
      Automated order updates (texts only): {AUTO_PHONE_DISPLAY}
    </p>
  </LegalLayout>
);

export default TermsOfService;
